/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { TicketListRow, type TicketListRowTrailingPill } from '../../shared/ticket/TicketListRow';
import { purchaseOrderStatusLabel } from '../domain/purchaseLabels';
import type { PurchaseRowPaymentPill } from '../domain/purchaseQueues';
import type { PurchaseOrderRow } from '../domain/purchaseTypes';

import { formatMoney } from '../../shared/format/public-api';
type Props = {
  item: PurchaseOrderRow;
  /** Pill de pago ('Por pagar' warn / 'Vencida' bad) según gastos ligados. */
  payment?: PurchaseRowPaymentPill | null;
  /** RI: hay gasto linked sin comprobante fiscal. */
  missingFiscal?: boolean;
  onOpenDetail: (order: PurchaseOrderRow) => void;
};


function statusTone(order: PurchaseOrderRow): 'ok' | 'warn' | 'bad' | 'muted' {
  switch (order.status) {
    case 'received':
      return 'ok';
    case 'partial':
      return 'warn';
    case 'cancelled':
      return 'bad';
    case 'sent':
      return 'warn';
    default:
      return 'muted';
  }
}

function supplierInitials(name: string | null): string {
  const clean = (name ?? '').trim();
  if (!clean) return '—';
  const parts = clean.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + second).toUpperCase();
}

function shortDate(iso: string | null | undefined): string | null {
  const date = (iso ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`;
}

export const PurchaseOrderRowCard = React.memo(function PurchaseOrderRowCard({
  item,
  payment,
  missingFiscal = false,
  onOpenDetail,
}: Props) {
  const cancelled = item.status === 'cancelled';
  const supplier = item.supplierName ?? 'Sin proveedor';
  const orderRef =
    item.orderNumber ?? (item.origin === 'direct' ? 'Compra directa' : 'Borrador');
  const date = shortDate(item.createdAt) ?? item.createdAt.slice(0, 10);

  const metaParts: string[] = [];
  if (cancelled) {
    metaParts.push(item.voidReason ? `Anulada: "${item.voidReason}"` : 'Anulada');
  } else {
    if (item.itemsCount != null && item.itemsCount > 0) {
      metaParts.push(`${item.itemsCount} ${item.itemsCount === 1 ? 'item' : 'items'}`);
    }
    if (item.orderedUnits != null && item.orderedUnits > 0) {
      metaParts.push(`Recibió ${item.receivedUnits ?? 0}/${item.orderedUnits}`);
    }
    const delivery = shortDate(item.expectedDelivery);
    if (delivery) metaParts.push(`Llega ${delivery}`);
    else if (item.supplierInvoiceRef) metaParts.push(`Fact. ${item.supplierInvoiceRef}`);
    if (payment && payment.pendingAmount > 0) {
      metaParts.push(`gasto pendiente ${formatMoney(payment.pendingAmount)}`);
    }
  }

  const trailingPills: TicketListRowTrailingPill[] = [];
  if (payment && !cancelled) {
    trailingPills.push({ label: payment.label, tone: payment.tone });
  }
  if (missingFiscal && !cancelled) {
    trailingPills.push({ label: 'Falta factura fiscal', tone: 'warn' });
  }
  if (item.origin === 'direct' && !cancelled) {
    trailingPills.push({ label: 'Directa', tone: 'brand' });
  }

  return (
    <TicketListRow
      title={supplier}
      subtitle={`${orderRef} · ${date}`}
      meta={metaParts.join(' · ') || null}
      value={formatMoney(item.total)}
      statusLabel={purchaseOrderStatusLabel(item.status)}
      statusTone={statusTone(item)}
      trailingPills={trailingPills}
      inactive={cancelled}
      avatar={{ initials: supplierInitials(item.supplierName), tone: 'muted' }}
      onPress={() => onOpenDetail(item)}
    />
  );
});
